/**
 * SellerAnalyticsPage - Dashboard for seller performance metrics
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  BarChart3, TrendingUp, Eye, DollarSign, Star, 
  Package, ShoppingCart, Award, ArrowUpRight, ArrowDownRight,
  BookOpen, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Metric Card Component
const MetricCard = ({ title, value, icon: Icon, color, trend, suffix = '' }) => (
  <Card>
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-3xl font-bold mt-1">{value}{suffix}</p>
          {trend !== undefined && (
            <p className={`text-sm mt-1 flex items-center gap-1 ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {trend >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              {Math.abs(trend)}% vs last month
            </p>
          )}
        </div>
        <div className={`p-4 rounded-xl ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Top Listing Card
const TopListingCard = ({ listing, rank, currencySymbol }) => (
  <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold">
      {rank}
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-medium truncate">{listing.title}</p>
      <p className="text-sm text-gray-500">{currencySymbol}{listing.price?.toLocaleString()}</p>
    </div>
    <div className="text-right">
      <p className="font-semibold">{listing.views}</p>
      <p className="text-xs text-gray-500">views</p>
    </div>
  </div>
);

export default function SellerAnalyticsPage() {
  const navigate = useNavigate();
  const { country } = useCountry();
  
  const [email, setEmail] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  
  const currencySymbol = country === 'UK' ? '£' : 'Rs.';
  
  const fetchAnalytics = async () => {
    if (!email) {
      toast.error('Please enter your seller email');
      return;
    }
    
    setLoading(true);
    setSearched(true);
    
    try {
      const response = await fetch(`${API_URL}/api/books-media/analytics/seller/${email}?country=${country}`);
      const data = await response.json();
      setAnalytics(data);
    } catch (err) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 bg-white/20 rounded-xl">
              <BarChart3 className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Seller Analytics</h1>
              <p className="text-white/80">Track your performance and grow your sales</p>
            </div>
          </div>
          
          {/* Email Input */}
          <div className="flex gap-3 max-w-md">
            <Input
              type="email"
              placeholder="Enter your seller email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-white text-gray-900"
              onKeyDown={(e) => e.key === 'Enter' && fetchAnalytics()}
            />
            <Button onClick={fetchAnalytics} className="bg-white text-purple-600 hover:bg-gray-100">
              View Analytics
            </Button>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
          </div>
        ) : !searched ? (
          <Card className="max-w-2xl mx-auto text-center py-16">
            <CardContent>
              <BarChart3 className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h2 className="text-xl font-semibold mb-2">Enter Your Email</h2>
              <p className="text-gray-500">Enter your seller email above to view your analytics dashboard</p>
            </CardContent>
          </Card>
        ) : !analytics?.metrics?.total_listings ? (
          <Card className="max-w-2xl mx-auto text-center py-16">
            <CardContent>
              <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h2 className="text-xl font-semibold mb-2">No Data Yet</h2>
              <p className="text-gray-500 mb-6">Start selling to see your analytics!</p>
              <Button onClick={() => navigate('/books-media/post')}>
                <BookOpen className="w-4 h-4 mr-2" />
                List Your First Item
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <MetricCard
                title="Total Listings"
                value={analytics.metrics.total_listings}
                icon={Package}
                color="bg-blue-500"
              />
              <MetricCard
                title="Active Listings"
                value={analytics.metrics.active_listings}
                icon={BookOpen}
                color="bg-green-500"
              />
              <MetricCard
                title="Total Views"
                value={analytics.metrics.total_views?.toLocaleString()}
                icon={Eye}
                color="bg-purple-500"
              />
              <MetricCard
                title="Total Sales"
                value={analytics.metrics.total_sales}
                icon={ShoppingCart}
                color="bg-amber-500"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <MetricCard
                title="Total Revenue"
                value={`${currencySymbol}${analytics.metrics.total_revenue?.toLocaleString()}`}
                icon={DollarSign}
                color="bg-emerald-500"
              />
              <MetricCard
                title="Avg Rating"
                value={analytics.metrics.avg_rating || 0}
                icon={Star}
                color="bg-yellow-500"
                suffix="/5"
              />
              <MetricCard
                title="Reviews"
                value={analytics.metrics.review_count || 0}
                icon={Award}
                color="bg-pink-500"
              />
              <MetricCard
                title="Conversion Rate"
                value={analytics.metrics.total_listings > 0 
                  ? ((analytics.metrics.total_sales / analytics.metrics.total_listings) * 100).toFixed(1)
                  : 0
                }
                icon={TrendingUp}
                color="bg-cyan-500"
                suffix="%"
              />
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Listings */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-amber-500" />
                    Top Performing Listings
                  </CardTitle>
                  <CardDescription>Your most viewed items</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {analytics.top_listings?.length > 0 ? (
                    analytics.top_listings.map((listing, i) => (
                      <TopListingCard 
                        key={listing.id} 
                        listing={listing} 
                        rank={i + 1}
                        currencySymbol={currencySymbol}
                      />
                    ))
                  ) : (
                    <p className="text-gray-500 text-center py-4">No listing data yet</p>
                  )}
                </CardContent>
              </Card>
              
              {/* Category Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-blue-500" />
                    Category Distribution
                  </CardTitle>
                  <CardDescription>Your listings by category</CardDescription>
                </CardHeader>
                <CardContent>
                  {analytics.category_distribution && Object.keys(analytics.category_distribution).length > 0 ? (
                    <div className="space-y-3">
                      {Object.entries(analytics.category_distribution).map(([cat, count]) => (
                        <div key={cat} className="flex items-center gap-3">
                          <div className="flex-1">
                            <div className="flex justify-between mb-1">
                              <span className="text-sm font-medium capitalize">{cat.replace(/-/g, ' ')}</span>
                              <span className="text-sm text-gray-500">{count}</span>
                            </div>
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
                                style={{ width: `${(count / analytics.metrics.total_listings) * 100}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No category data yet</p>
                  )}
                </CardContent>
              </Card>
            </div>
            
            {/* Quick Actions */}
            <Card className="mt-6">
              <CardContent className="p-6">
                <div className="flex flex-wrap gap-4 justify-center">
                  <Button onClick={() => navigate('/books-media/post')}>
                    <BookOpen className="w-4 h-4 mr-2" />
                    List New Item
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/books-media/subscriptions')}>
                    <Award className="w-4 h-4 mr-2" />
                    Upgrade Plan
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/books-media')}>
                    <Eye className="w-4 h-4 mr-2" />
                    View Marketplace
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
